import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  MdAdd,
  MdContentCopy,
  MdOutlineAutorenew,
  MdOutlineEdit,
  MdOutlineEmail,
  MdOutlineOpenInNew,
  MdOutlineRefresh,
  MdOutlineWhatsapp,
} from "react-icons/md";
import axios from "../../config/axiosInstance";
import { SUBSCRIPTIONS } from "../../config/apiConfig";
import { useApplication } from "../../contexts/ApplicationContext";
import Modal from "../modals/Modal";
import { Badge, Card, ErrorBlock, SkeletonRows } from "../crm/Primitives";
import { formatDate, formatMoney, whatsappLink } from "../crm/crmHelpers";
import SubscriptionFormModal from "./SubscriptionFormModal";
import { CHARGE_TONES, STATUS_TONES, isLiveSubscription } from "./subscriptionHelpers";

/*
  A assinatura de uma conta: situação, link de pagamento, cobranças e ações. É o
  mesmo painel na ficha do cliente e na tela de Assinaturas.

  O link é o que fecha a venda: a assinatura criada pela equipe só começa a valer
  quando a cliente paga a primeira fatura, então copiar e mandar precisa estar à
  mão. O servidor só manda e-mail quando alguém pede, para a equipe poder conferir
  antes de a cliente receber.

  Quem não é ADMIN/OPERATOR (o gerente de contas) vê tudo e não age: quem autoriza
  é o servidor, aqui só não se oferece o botão que seria recusado.
*/

const Row = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <span className="shrink-0 text-xs text-slate-500">{label}</span>
    <span className="min-w-0 text-right text-xs font-semibold text-slate-800">{children}</span>
  </div>
);

const durationText = (subscription) => {
  if (!subscription.endDate) return "Sem prazo (cobra até cancelar)";
  const months = subscription.durationMonths;
  if (!months) return `Até ${formatDate(subscription.endDate)}`;
  return `${months} ${months === 1 ? "mês" : "meses"}, até ${formatDate(
    subscription.termEndDate
  )} · última cobrança em ${formatDate(subscription.lastChargeDate)}`;
};

const describeTeam = ({ planName, planId, numberOfUsers }) =>
  `${planName}${planId === "DIAMOND" ? " (equipe inteira)" : ` · ${numberOfUsers} profissional(is)`}`;

const SubscriptionPanel = ({ company, embedded = false }) => {
  const queryClient = useQueryClient();
  const { isAdminOrOperator } = useApplication();

  const [formOpen, setFormOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState(null);

  const queryKey = ["subscriptions", "company", String(company.id)];

  const query = useQuery(queryKey, async () => {
    const { data } = await axios.get(`${SUBSCRIPTIONS.FOR_COMPANY}/${company.id}`);
    return data.data.subscription;
  });

  const subscription = query.data;
  const scheduled = subscription?.scheduledChange;
  const isLive = isLiveSubscription(subscription);

  const refresh = () => {
    queryClient.invalidateQueries(["subscriptions"]);
    queryClient.invalidateQueries(["client", "detail", String(company.id)]);
    queryClient.invalidateQueries(["crm"]);
    queryClient.invalidateQueries(["companies"]);
  };

  const actionOptions = (fallback, onDone) => ({
    onSuccess: (response) => {
      setError(null);
      onDone?.(response);
      refresh();
    },
    onError: (err) => {
      setFeedback(null);
      setError(err.response?.data?.message || fallback);
    },
  });

  const sync = useMutation(
    () => axios.post(`${SUBSCRIPTIONS.ONE}/${subscription.id}/sync`),
    actionOptions("Não foi possível conferir no Asaas.", () =>
      setFeedback("Conferido com o Asaas.")
    )
  );

  const sendLink = useMutation(
    () => axios.post(`${SUBSCRIPTIONS.ONE}/${subscription.id}/send-link`),
    actionOptions("Não foi possível enviar o link.", ({ data }) =>
      setFeedback(`Link enviado para ${data.data.sentTo}.`)
    )
  );

  const cancel = useMutation(
    () =>
      axios.post(`${SUBSCRIPTIONS.ONE}/${subscription.id}/cancel`, {
        reason: cancelReason.trim() || undefined,
      }),
    actionOptions("Não foi possível cancelar a assinatura.", () => {
      setCancelOpen(false);
      setCancelReason("");
      setFeedback("Assinatura cancelada. O acesso segue até o fim do período pago.");
    })
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(subscription.paymentUrl);
      setError(null);
      setFeedback("Link copiado.");
    } catch {
      setError("Não foi possível copiar. Selecione o link e copie à mão.");
    }
  };

  const baseWhatsapp = whatsappLink(company.phoneNumber);
  const whatsappHref =
    baseWhatsapp && subscription?.paymentUrl
      ? `${baseWhatsapp}?text=${encodeURIComponent(
          `Olá! Segue o link para concluir o pagamento da sua assinatura Relinq: ${subscription.paymentUrl}`
        )}`
      : null;

  const charges = subscription?.charges || [];

  const content = (
    <>
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-100 px-4 py-3">
        <div>
          <h2 className="flex items-center gap-1.5 text-sm font-bold text-slate-900">
            <MdOutlineAutorenew size={16} /> Assinatura
          </h2>
          <p className="text-[11px] text-slate-500">Cobrança recorrente no Asaas</p>
        </div>

        {subscription ? (
          <Badge tone={STATUS_TONES[subscription.status] || "neutral"}>
            {subscription.statusLabel}
          </Badge>
        ) : null}
      </div>

      {error ? (
        <div className="px-4 pt-3">
          <ErrorBlock message={error} />
        </div>
      ) : null}
      {feedback ? (
        <p className="mx-4 mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 ring-1 ring-inset ring-emerald-200">
          {feedback}
        </p>
      ) : null}

      {query.isLoading ? (
        <div className="p-4">
          <SkeletonRows rows={4} />
        </div>
      ) : query.error ? (
        <div className="p-4">
          <ErrorBlock
            message={
              query.error.response?.data?.message ||
              "Não foi possível consultar a assinatura."
            }
            onRetry={query.refetch}
          />
        </div>
      ) : (
        <>
          {subscription ? (
            <div className="divide-y divide-slate-100 px-4 py-2">
              <Row label="Produto">{describeTeam(scheduled ? subscription.access : subscription)}</Row>
              {scheduled ? (
                <Row label="Alteração agendada">
                  {describeTeam(scheduled)} por {formatMoney(scheduled.value)}/mês, quando a cobrança
                  de {formatDate(scheduled.effectiveDate)} for paga
                </Row>
              ) : null}
              <Row label="Valor">
                {formatMoney(scheduled ? (subscription.access.value ?? subscription.value) : subscription.value)}
                /mês · ciclo {subscription.cycleLabel}
                {subscription.isNegotiatedValue ? (
                  <span className="block text-[11px] font-medium text-amber-700">
                    {subscription.couponCode ? "Com cupom" : "Negociado"} (tabela:{" "}
                    {formatMoney(subscription.catalogValue)}/mês)
                  </span>
                ) : null}
              </Row>
              <Row label="Forma de pagamento">{subscription.billingLabel}</Row>
              <Row label="Tempo de assinatura">{durationText(subscription)}</Row>
              <Row label="Próxima cobrança">
                {subscription.nextDueDate
                  ? formatDate(subscription.nextDueDate)
                  : subscription.canRenew
                  ? "Nenhuma: prazo todo cobrado"
                  : "—"}
              </Row>
              {subscription.couponCode ? (
                <Row label="Cupom">
                  {subscription.couponCode} (−{formatMoney(subscription.couponDiscountAmount)})
                </Row>
              ) : null}
              <Row label="Criada por">
                {subscription.createdByName || "—"}
                {subscription.origin === "APP" ? " (pelo app)" : ""} ·{" "}
                {formatDate(subscription.createdAt)}
              </Row>
              {subscription.cancelledAt ? (
                <Row label="Cancelada">
                  {formatDate(subscription.cancelledAt)}
                  {subscription.cancelledByName ? ` por ${subscription.cancelledByName}` : ""}
                  {subscription.cancelReason ? (
                    <span className="block text-[11px] font-medium text-slate-500">
                      {subscription.cancelReason}
                    </span>
                  ) : null}
                </Row>
              ) : null}
              {subscription.note ? <Row label="Observação">{subscription.note}</Row> : null}
            </div>
          ) : (
            <p className="px-4 py-6 text-center text-xs text-slate-400">
              Esta conta ainda não tem assinatura.
            </p>
          )}

          {isLive && subscription.paymentUrl ? (
            <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3">
              <p className="mb-1 text-[11px] font-semibold text-slate-600">
                {subscription.status === "PENDING"
                  ? "Link para a cliente concluir a assinatura"
                  : subscription.status === "OVERDUE"
                  ? "Link da cobrança em atraso"
                  : "Link da próxima cobrança"}
              </p>
              <p className="mb-2 break-all rounded-lg bg-white px-2 py-1.5 font-mono text-[11px] text-slate-600 ring-1 ring-inset ring-slate-200">
                {subscription.paymentUrl}
              </p>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={copyLink}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:border-pink-200"
                >
                  <MdContentCopy size={13} /> Copiar
                </button>
                {whatsappHref ? (
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-[11px] font-semibold text-white transition hover:bg-emerald-700"
                  >
                    <MdOutlineWhatsapp size={13} /> WhatsApp
                  </a>
                ) : null}
                {isAdminOrOperator ? (
                  <button
                    type="button"
                    onClick={() => sendLink.mutate()}
                    disabled={sendLink.isLoading}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:border-pink-200 disabled:opacity-40"
                  >
                    <MdOutlineEmail size={13} />
                    {sendLink.isLoading ? "Enviando..." : "Enviar por e-mail"}
                  </button>
                ) : null}
                <a
                  href={subscription.paymentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:border-pink-200"
                >
                  <MdOutlineOpenInNew size={13} /> Abrir
                </a>
              </div>
            </div>
          ) : null}

          {isAdminOrOperator ? (
            <div className="flex flex-wrap items-center gap-1.5 border-t border-slate-100 px-4 py-3">
              {isLive ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setFeedback(null);
                      setFormOpen(true);
                    }}
                    className="inline-flex items-center gap-1 rounded-lg border border-pink-200 px-2.5 py-1.5 text-[11px] font-semibold text-pink-700 transition hover:bg-pink-50"
                  >
                    <MdOutlineEdit size={13} /> Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => sync.mutate()}
                    disabled={sync.isLoading}
                    title="Confere direto no Asaas, sem esperar o aviso automático"
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:border-pink-200 disabled:opacity-40"
                  >
                    <MdOutlineRefresh size={13} />
                    {sync.isLoading ? "Conferindo..." : "Conferir no Asaas"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      setCancelOpen(true);
                    }}
                    className="ml-auto rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-rose-600 transition hover:bg-rose-50"
                  >
                    Cancelar assinatura
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setFeedback(null);
                    setFormOpen(true);
                  }}
                  className="inline-flex items-center gap-1 rounded-lg bg-pink-600 px-3 py-1.5 text-[11px] font-semibold text-white transition hover:bg-pink-700"
                >
                  <MdAdd size={14} /> Criar assinatura
                </button>
              )}
            </div>
          ) : null}

          {charges.length ? (
            <div className="border-t border-slate-100">
              <p className="px-4 pt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Cobranças
              </p>
              <ul className="divide-y divide-slate-100">
                {charges.slice(0, 12).map((charge) => (
                  <li key={charge.id} className="flex items-center gap-3 px-4 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-slate-800">
                        {formatMoney(charge.value)}
                      </p>
                      <p className="truncate text-[11px] text-slate-500">
                        Vence {formatDate(charge.dueDate)} · {charge.billingLabel}
                        {charge.paidAt ? ` · paga em ${formatDate(charge.paidAt)}` : ""}
                        {charge.newExpirationDate
                          ? ` · acesso até ${formatDate(charge.newExpirationDate)}`
                          : ""}
                      </p>
                    </div>
                    <Badge tone={CHARGE_TONES[charge.status] || "neutral"}>
                      {charge.statusLabel}
                    </Badge>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}

      {formOpen ? (
        <SubscriptionFormModal
          company={company}
          subscription={isLive ? subscription : null}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            setFormOpen(false);
            setFeedback(isLive ? "Assinatura atualizada." : "Assinatura criada. Mande o link para a cliente.");
            refresh();
          }}
        />
      ) : null}

      <Modal
        isOpen={cancelOpen}
        onClose={() => !cancel.isLoading && setCancelOpen(false)}
        size="sm"
        title="Cancelar assinatura"
        subtitle={company.name}
        closeOnBackdrop={!cancel.isLoading}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setCancelOpen(false)}
              disabled={cancel.isLoading}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
            >
              Voltar
            </button>
            <button
              type="button"
              onClick={() => cancel.mutate()}
              disabled={cancel.isLoading}
              className="rounded-xl bg-rose-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-rose-700 disabled:opacity-40"
            >
              {cancel.isLoading ? "Cancelando..." : "Cancelar assinatura"}
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          {error ? <ErrorBlock message={error} /> : null}
          <p className="text-sm text-slate-700">
            A recorrência é encerrada no Asaas e não dá para reativar: as cobranças em aberto
            somem e não haverá novas. O acesso da conta segue até o fim do período já pago.
          </p>
          <div>
            <label
              htmlFor="subscription-cancel-reason"
              className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400"
            >
              Motivo (fica no histórico)
            </label>
            <textarea
              id="subscription-cancel-reason"
              rows={2}
              maxLength={500}
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
              className="w-full resize-y rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
            />
          </div>
        </div>
      </Modal>
    </>
  );

  return embedded ? <div>{content}</div> : <Card className="overflow-hidden">{content}</Card>;
};

export default SubscriptionPanel;
